import { useCallback, useMemo } from 'react';
import { SongService } from '../service/song/SongService';
import { getSongService } from '../service/ServiceRegistry';
import type {
  Song,
  SongsQueryParams,
  CreateTrackRequest,
  UpdateTrackRequest,
} from '../types/api/song';
import type { FetchDataFn, TableDataResponse, TableQueryParams } from '../types/table';

export function useSongs() {
  const service = useMemo(() => new SongService(), []);

  const fetchSongs: FetchDataFn<Song> = useCallback(
    async (params: TableQueryParams): Promise<TableDataResponse<Song>> => {
      const query: SongsQueryParams = {
        page: params.page,
        size: params.pageSize,
        keyword: params.search,
      };
      const response = await service.getSongs(query);
      return {
        data: response.items,
        pagination: {
          page: response.page,
          pageSize: response.size,
          total: response.totalItems,
          totalPages: response.totalPages,
          hasNext: response.hasNext,
          hasPrev: response.hasPrevious,
        },
      };
    },
    [service]
  );

  return { fetchSongs };
}

export function useSongActions() {
  const service = getSongService();

  const createTrack = useCallback(
    async (data: CreateTrackRequest, audioFile?: File): Promise<Song> => {
      return service.createTrack(data, audioFile);
    },
    [service]
  );

  const updateTrack = useCallback(
    async (trackId: string, data: UpdateTrackRequest, audioFile?: File): Promise<Song> => {
      return service.updateTrack(trackId, data, audioFile);
    },
    [service]
  );

  const getTrack = useCallback(
    async (trackId: string): Promise<Song> => {
      return service.getSong(trackId);
    },
    [service]
  );

  const deleteTrack = useCallback(
    async (trackId: string): Promise<void> => {
      return service.deleteTrack(trackId);
    },
    [service]
  );

  return { createTrack, updateTrack, getTrack, deleteTrack };
}
